import Icon, { FilePdfOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Dropdown, Menu, message, Modal, Row, Select, Space, Spin, Tooltip } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import checklistApi from '../../../../../api/ChecklistApi';
import checklistDetailsReportApi from '../../../../../api/ChecklistDetailsReportApi';
import hubApi from '../../../../../api/HubApi';
import projectApi from '../../../../../api/ProjectApi';
import LayoutComponent from '../../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../../context/CustomContext';
import { Checklist, Hub, Project } from '../../../../../model/entities';
import { ChecklistDetailsReport } from '../../../../../model/entities-report';
import { ReactComponent as BackSvg } from '../../../../../resources/images/back.svg';
import { ReactComponent as CaretDownSvg } from '../../../../../resources/images/caret-down.svg';
import { ReactComponent as SaveSvg } from '../../../../../resources/images/save.svg';
import { ReactComponent as TrashCanSvg } from '../../../../../resources/images/trash-can.svg';
import { ReactComponent as WarningSvg } from '../../../../../resources/images/warning.svg';
import notificationService from '../../../../../services/NotificationService';
import stringService from '../../../../../services/StringService';
import { ChecklistDetailsReportModal } from './ChecklistDetailsReportModal/ChecklistDetailsReportModal';
import styles from './ChecklistDetailsReportPage.module.scss';

class ChecklistDetailsReportPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            hubs: [],
            projects: [],
            checklists: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    /**
     * Initialiazes the page.
     */
    init = async () => {
        try {
            if (this.props.match.params.id === 'new') {
                await this.new();
            } else {
                await this.get(+this.props.match.params.id);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Initialiazes the page for report creation.
     */
    new = async () => {
        this.setState({ loading: 'loadingHubs' });
        const hubs = await hubApi.list(true);
        const hub = hubs[0];
        const projects = await projectApi.list(hub.provider, hub.id);
        const report: ChecklistDetailsReport = {};
        this.setState({ hubs, hub, projects, report });
    };

    /**
     * Initialiazes the page for report edition.
     */
    get = async (id: number) => {
        this.setState({ loading: 'loading' });

        let hubs: Hub[];
        let hub: Hub | undefined;
        let projects: Project[] = [];
        let project: Project | undefined;
        let checklists: Checklist[] = [];
        let checklist: Checklist | undefined;

        const report = await checklistDetailsReportApi.get(id);
        hubs = await hubApi.list(true);
        if (hubs.map((h) => h.id).includes(report.hubId!)) {
            hub = hubs.filter((h) => h.id === report.hubId)[0];
            projects = await projectApi.list(hub.provider, hub.id);
            if (projects.map((p) => p.id).includes(report.projectId!)) {
                project = projects.filter((p) => p.id === report.projectId)[0];
                checklists = (await checklistApi.list(0, 100, 'title', true, hub!.id, project.id!)).content;
                if (checklists.map((c) => c.id).includes(report.checklistId!)) {
                    checklist = checklists.filter((c) => c.id === report.checklistId)[0];
                }
            }
        }

        this.setState({ report, hubs, hub, projects, project, checklists, checklist });
    };

    /**
     * Selects a hub.
     * @param hubId - the hub id
     */
    selectHub = async (hubId: string) => {
        const { hubs } = this.state;
        try {
            this.setState({ loading: 'loadingProjects' });
            const hub = hubs.find((p) => p.id === hubId);
            const projects = await projectApi.list(hub!.provider, hub!.id);
            const project = undefined;
            const checklists: Checklist[] = [];
            const checklist = undefined;
            this.setState({ hub, projects, project, checklists, checklist });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Selects a project.
     * @param projectId - the project id
     */
    selectProject = async (projectId: string) => {
        const { hub, projects } = this.state;
        try {
            this.setState({ loading: 'loadingChecklists' });
            const project = projects.find((p) => p.id === projectId);
            const checklists = (await checklistApi.list(0, 100, 'title', true, hub!.id, project!.id)).content;
            const checklist = undefined;
            this.setState({ project, checklists, checklist });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Selects a checklist.
     * @param checklistId - the checklist id
     */
    selectChecklist = async (checklistId: string) => {
        const { checklists } = this.state;
        try {
            const checklist = checklists.find((c) => c.id === checklistId);
            this.setState({ checklist });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /**
     * Returns a report.
     */
    build = async () => {
        const { hub, project, checklist } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const result = await checklistDetailsReportApi.build(hub!.id, project!.id, checklist!.id);
            const report: ChecklistDetailsReport = Object.assign({}, this.state.report, {
                result,
                hubId: hub?.id,
                projectId: project?.id,
                checklistId: checklist?.id,
            });

            this.setState({ report });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Saves the report.
     * @param values  - the report values
     */
    save = async (values: any) => {
        try {
            let report: ChecklistDetailsReport = Object.assign({}, this.state.report, values);
            report.result = undefined;
            report = report.id
                ? await checklistDetailsReportApi.update(report)
                : await checklistDetailsReportApi.create(report);
            report.result = this.state.report?.result;
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            this.showSaveModal(false);
            this.setState({ report });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /**
     * Deletes the report.
     */
    delete = async () => {
        const { report } = this.state;

        try {
            await checklistDetailsReportApi.delete(report!.id!);
            this.props.history.push(`/reports/checklist-details`);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Shows the save report modal.
     */
    showSaveModal = (visible: boolean) => {
        const saveModalVisible = visible;
        this.setState({ saveModalVisible });
    };

    /**
     * Shows the delete report modal.
     */
    showDeleteModal = () => {
        const { intl } = this.props;
        Modal.confirm({
            title: intl.formatMessage({ id: 'reports.delete.modal.title' }),
            icon: <Icon component={WarningSvg} />,
            okText: intl.formatMessage({ id: 'button.yes' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'button.no' }),
            onOk: () => this.delete(),
        });
    };

    /**
     * Returns if the save button is disabled.
     * @returns if the save button is disabled
     */
    isSaveButtonDisabled = (): boolean => {
        const { report } = this.state;
        return !report || !report.result;
    };

    /**
     * Returns if the delete button is disabled.
     * @returns if the delete button is disabled
     */
    isDeleteButtonDisabled = (): boolean => {
        const { report } = this.state;
        return !report || !report.id;
    };

    /*** COMPONENTS ***/

    renderSelector = (): React.ReactElement | undefined => {
        const { hubs, hub, projects, project, checklists, checklist, loading, report } = this.state;
        const hubOptions = hubs.map((hub) => (
            <Select.Option key={hub.id} value={hub.id}>
                {`${hub.provider} > ${hub.name}`}
            </Select.Option>
        ));
        const projectOptions = projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));
        const checklistOptions = checklists
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((checklist) => (
                <Select.Option key={checklist.id} value={checklist.id}>
                    {checklist.identifier} - {checklist.title}
                </Select.Option>
            ));

        return (
            <div className={styles.selector}>
                <Row gutter={[48, 24]} align="bottom">
                    <Col span={7} className={styles.field}>
                        <FormattedMessage id="reports.hub" tagName="label" />
                        <Select
                            size="large"
                            className={styles.hubs}
                            value={hub?.id}
                            onChange={this.selectHub}
                            placeholder={<FormattedMessage id="reports.hub.placeholder" />}
                            loading={loading === 'loadingHubs'}
                            disabled={report && !!report.id}
                        >
                            {hubOptions}
                        </Select>
                    </Col>
                    <Col span={10} className={styles.field}>
                        <FormattedMessage id="reports.project" tagName="label" />
                        <Select
                            size="large"
                            className={styles.projects}
                            value={project?.id}
                            onChange={this.selectProject}
                            placeholder={<FormattedMessage id="reports.project.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                            loading={loading === 'loadingProjects'}
                            disabled={report && !!report.id}
                        >
                            {projectOptions}
                        </Select>
                    </Col>
                    <Col span={7} className={styles.button}>
                        <Button
                            size="large"
                            type={this.isSaveButtonDisabled() ? 'primary' : 'default'}
                            icon={<ThunderboltOutlined />}
                            onClick={this.build}
                            loading={loading === 'loading'}
                            disabled={!checklist || (report && !!report.id)}
                            block
                        >
                            <FormattedMessage id="reports.create.button" tagName="span" />
                        </Button>
                    </Col>
                    <Col span={17} className={styles.field}>
                        <FormattedMessage id="reports.checklist" tagName="label" />
                        <Select
                            size="large"
                            className={styles.checklists}
                            value={checklist?.id}
                            onChange={this.selectChecklist}
                            placeholder={<FormattedMessage id="reports.checklist.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                            loading={loading === 'loadingChecklists'}
                            disabled={report && !!report.id}
                        >
                            {checklistOptions}
                        </Select>
                    </Col>
                </Row>

                <Divider />
            </div>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { report, loading } = this.state;

        return (
            <div className="toolbar report">
                <div className="title">
                    <h2>{report?.name || <FormattedMessage id="checklistDetailsReport.title" />}</h2>
                </div>
                <Space>
                    <Tooltip title={<FormattedMessage id="button.back" />}>
                        <Link to="/reports/checklist-details">
                            <Button type="ghost" size="large" icon={<Icon component={BackSvg} />} />
                        </Link>
                    </Tooltip>
                    <Tooltip title={<FormattedMessage id="button.exportPdf" />}>
                        <Button
                            size="large"
                            icon={<FilePdfOutlined />}
                            loading={loading === 'exporting'}
                            disabled
                        ></Button>
                    </Tooltip>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={this.isSaveButtonDisabled()}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="company"
                                    icon={<Icon component={SaveSvg} />}
                                    onClick={() => this.showSaveModal(true)}
                                    disabled={this.isSaveButtonDisabled()}
                                >
                                    <FormattedMessage id="button.save" />
                                </Menu.Item>
                                <Menu.Item
                                    key="inactivate"
                                    icon={<Icon component={TrashCanSvg} />}
                                    onClick={this.showDeleteModal}
                                    disabled={this.isDeleteButtonDisabled()}
                                >
                                    <FormattedMessage id="button.delete" />
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="primary" size="large">
                            <FormattedMessage id="button.actions" /> <Icon component={CaretDownSvg} />
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        );
    };

    renderSaveModal = (): React.ReactElement | undefined => {
        const { report, saveModalVisible } = this.state;

        return (
            <ChecklistDetailsReportModal
                visible={saveModalVisible}
                report={report}
                onSave={this.save}
                onCancel={() => this.showSaveModal(false)}
            />
        );
    };

    renderReport = (): React.ReactElement | undefined => {
        const { report, loading } = this.state;

        if (loading === 'loading') {
            return (
                <div className={styles.spin}>
                    <Spin size="large" />
                </div>
            );
        } else if (report && report.result) {
            return <div></div>;
        }
    };

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderSelector()}
                {this.renderToolbar()}
                {this.renderSaveModal()}
                {this.renderReport()}
            </>
        );
    };

    render() {
        return <LayoutComponent pageId="reports">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(ChecklistDetailsReportPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    hubs: Hub[];
    hub?: Hub;
    projects: Project[];
    project?: Project;
    checklists: Checklist[];
    checklist?: Checklist;
    report?: ChecklistDetailsReport;
    saveModalVisible?: boolean;
    loading?: 'loadingHubs' | 'loadingProjects' | 'loadingChecklists' | 'loading' | 'exporting';
}
